import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore, useMovieStore } from '../stores/movieStore';
import type { Movie } from '../stores/movieStore';

const AdminPage = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { movies, addMovie, removeMovie } = useMovieStore();
  const [activeTab, setActiveTab] = useState<'stats' | 'movies' | 'sources' | 'users'>('stats');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMovie, setNewMovie] = useState({ title: '', year: 2024, rating: 7, poster: '', genre: '', description: '' });

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">无访问权限</h2>
          <p className="text-gray-400 mb-4">需要管理员权限才能访问</p>
          <Link to="/" className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl">返回首页</Link>
        </div>
      </div>
    );
  }

  const stats = [
    { label: '总影视数', value: '1,234', icon: '🎬', color: 'from-blue-500 to-blue-600' },
    { label: '总用户数', value: '5,678', icon: '👥', color: 'from-green-500 to-green-600' },
    { label: '播放源数', value: '89', icon: '🔗', color: 'from-purple-500 to-purple-600' },
    { label: '今日访问', value: '12,345', icon: '📊', color: 'from-orange-500 to-orange-600' },
  ];

  const tabs = [
    { id: 'stats', label: '数据统计', icon: '📊' },
    { id: 'movies', label: '影视管理', icon: '🎬' },
    { id: 'sources', label: '播放源管理', icon: '🔗' },
    { id: 'users', label: '用户管理', icon: '👥' },
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">管理后台</h1>
          <p className="text-gray-400 mt-2">欢迎回来，{user?.username}</p>
        </div>

        {/* 标签页 */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all ${activeTab === tab.id ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : 'bg-slate-800/50 text-gray-400 hover:text-white hover:bg-slate-700/50'}`}>
              <span>{tab.icon}</span><span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* 数据统计 */}
        {activeTab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div key={stat.label} className="glass rounded-2xl p-6 card-hover">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl">{stat.icon}</span>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                    <span className="text-white text-xl">+</span>
                  </div>
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* 影视管理 */}
        {activeTab === 'movies' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">影视列表</h2>
              <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl">添加影视</button>
            </div>
            <div className="glass rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-slate-700">
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">影视名称</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">年份</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">评分</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">播放源</th>
                  <th className="px-6 py-4 text-right text-gray-400 font-medium">操作</th>
                </tr></thead>
                <tbody>
                  {[{ id: '1', title: '流浪地球2', year: 2023, rating: 8.3, sources: 3 }, { id: '2', title: '三体', year: 2023, rating: 8.5, sources: 2 }, { id: '3', title: '狂飙', year: 2023, rating: 8.7, sources: 4 }].map((m) => (
                    <tr key={m.id} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-white">{m.title}</td>
                      <td className="px-6 py-4 text-gray-300">{m.year}</td>
                      <td className="px-6 py-4 text-yellow-400">{m.rating}</td>
                      <td className="px-6 py-4 text-gray-300">{m.sources} 个</td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button className="px-3 py-1 text-blue-400 hover:text-blue-300">编辑</button>
                        <button className="px-3 py-1 text-red-400 hover:text-red-300">删除</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 播放源管理 */}
        {activeTab === 'sources' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">播放源列表</h2>
              <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl">添加播放源</button>
            </div>
            <div className="glass rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-slate-700">
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">源名称</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">API地址</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">状态</th>
                  <th className="px-6 py-4 text-right text-gray-400 font-medium">操作</th>
                </tr></thead>
                <tbody>
                  {[{ name: '资源站A', url: 'https://api.example.com', status: '正常' }, { name: '资源站B', url: 'https://api.example2.com', status: '正常' }, { name: '资源站C', url: 'https://api.example3.com', status: '维护中' }].map((s, i) => (
                    <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-white">{s.name}</td>
                      <td className="px-6 py-4 text-gray-400 font-mono text-sm">{s.url}</td>
                      <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${s.status === '正常' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{s.status}</span></td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button className="px-3 py-1 text-blue-400 hover:text-blue-300">测试</button>
                        <button className="px-3 py-1 text-red-400 hover:text-red-300">删除</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 用户管理 */}
        {activeTab === 'users' && (
          <div>
            <h2 className="text-xl font-bold text-white mb-6">用户列表</h2>
            <div className="glass rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead><tr className="border-b border-slate-700">
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">用户名</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">邮箱</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">角色</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-medium">注册时间</th>
                  <th className="px-6 py-4 text-right text-gray-400 font-medium">操作</th>
                </tr></thead>
                <tbody>
                  {[{ username: 'admin', email: 'admin@example.com', role: '管理员', date: '2024-01-01' }, { username: 'user1', email: 'user1@example.com', role: '用户', date: '2024-01-15' }].map((u, i) => (
                    <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-white">{u.username}</td>
                      <td className="px-6 py-4 text-gray-400">{u.email}</td>
                      <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${u.role === '管理员' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>{u.role}</span></td>
                      <td className="px-6 py-4 text-gray-400">{u.date}</td>
                      <td className="px-6 py-4 text-right"><button className="px-3 py-1 text-blue-400 hover:text-blue-300">编辑</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
